// Storage helpers for the Virtual Announcement feature.
//
// This is intentionally kept separate from BellStorageUtils / BellScheduler so
// the existing bell schedule logic is never touched. Announcement schedules and
// recording metadata live in localStorage (so the scheduler can read them
// synchronously every tick), while the actual recorded audio blobs live in
// IndexedDB (they are too large for the localStorage quota).

import {
    listVirtualAnnouncements,
    queueVirtualAnnouncementUpsert,
    saveVirtualAnnouncements,
} from "../Services/VirtualAnnouncementService";

export const ANNOUNCEMENT_SCHEDULES_STORAGE_KEY = "announcementSchedules";
export const ANNOUNCEMENT_RECORDINGS_META_KEY = "announcementRecordings";
export const ANNOUNCEMENT_HISTORY_STORAGE_KEY = "announcementBroadcastHistory";
export const ANNOUNCEMENT_SCHEDULES_UPDATED_EVENT = "announcement:schedules-updated";
export const ANNOUNCEMENT_RECORDINGS_UPDATED_EVENT = "announcement:recordings-updated";
export const ANNOUNCEMENT_HISTORY_UPDATED_EVENT = "announcement:history-updated";

// Cap the broadcast history so localStorage never grows without bound.
const HISTORY_LIMIT = 60;

const IDB_NAME = "bell-announcements";
const IDB_VERSION = 1;
const IDB_STORE = "recordings";

/* ----------------------------- schedules ----------------------------- */

export const readAnnouncementSchedules = () => {
    try {
        const data = JSON.parse(localStorage.getItem(ANNOUNCEMENT_SCHEDULES_STORAGE_KEY));
        return Array.isArray(data) ? data : [];
    } catch (e) {
        console.error("Error reading announcement schedules from local storage:", e);
        return [];
    }
};

export const notifyAnnouncementSchedulesUpdated = (schedules = readAnnouncementSchedules()) => {
    window.dispatchEvent(
        new CustomEvent(ANNOUNCEMENT_SCHEDULES_UPDATED_EVENT, {
            detail: {schedules, updatedAt: Date.now()},
        })
    );
};

export const setAnnouncementSchedules = (schedules = []) => {
    const next = Array.isArray(schedules) ? schedules : [];
    localStorage.setItem(ANNOUNCEMENT_SCHEDULES_STORAGE_KEY, JSON.stringify(next));
    notifyAnnouncementSchedulesUpdated(next);
    return next;
};

export const saveAnnouncementSchedule = (schedule) => {
    const schedules = readAnnouncementSchedules();
    const index = schedules.findIndex((item) => item.id === schedule.id);

    let merged;
    if (index >= 0) {
        merged = {...schedules[index], ...schedule};
        schedules[index] = merged;
    } else {
        merged = schedule;
        schedules.push(schedule);
    }

    // Mirror the change to the backend asynchronously. Fire-and-forget so a slow
    // or failed network never blocks the local save or the scheduler.
    queueVirtualAnnouncementUpsert(merged);

    return setAnnouncementSchedules(schedules);
};

export const deleteAnnouncementSchedule = (id) => {
    const schedules = readAnnouncementSchedules();
    const target = schedules.find((item) => item.id === id);

    // The backend batch endpoint has no hard delete, so a removal is mirrored as
    // a soft-delete (status "N"). Fire-and-forget, same as saves.
    if (target) {
        queueVirtualAnnouncementUpsert({...target, status: "N"});
    }

    return setAnnouncementSchedules(schedules.filter((item) => item.id !== id));
};

// Two-way reconcile with the backend for the logged-in school. Runs once on
// load: (1) pushes every locally-stored announcement up so older installs that
// only ever saved to localStorage become backward-compatible with the backend,
// then (2) pulls the school's announcements and merges in any this device is
// missing (e.g. created on another device). Never throws — a sync failure must
// not affect playback or the UI.
export const syncAnnouncementsWithBackend = async () => {
    try {
        const local = readAnnouncementSchedules();

        if (local.length > 0) {
            try {
                await saveVirtualAnnouncements(local);
            } catch (e) {
                // Push failed (offline/server down); the pull below and the
                // per-write offline queue will catch up later.
            }
        }

        const remote = await listVirtualAnnouncements();
        if (Array.isArray(remote) && remote.length > 0) {
            const current = readAnnouncementSchedules();
            const byId = new Map(current.map((item) => [item.id, item]));
            let changed = false;

            remote.forEach((item) => {
                // Don't resurrect soft-deleted announcements onto this device.
                if (item?.id && item.status !== "N" && !byId.has(item.id)) {
                    byId.set(item.id, item);
                    changed = true;
                }
            });

            if (changed) {
                setAnnouncementSchedules(Array.from(byId.values()));
            }
        }
    } catch (e) {
        console.warn("Virtual announcement backend sync failed:", e);
    }
};

// Authoritative hydrate at login, mirroring how the bell queue is fetched. It
// pushes any local-only announcements up first (backward compatibility for
// installs that previously saved only to localStorage), then treats the
// backend as the source of truth and replaces the local store with the school's
// active announcements. The scheduler reads localStorage, so replacing it here
// reschedules everything dynamically per the latest backend response. Failures
// never wipe local data: on a fetch/push error we keep (or merge into) what we
// already have. Never throws.
export const hydrateAnnouncementsFromBackend = async () => {
    try {
        const local = readAnnouncementSchedules();

        // Backward compat: ensure device-only announcements exist on the backend
        // before we treat the backend list as authoritative.
        let pushedOk = true;
        if (local.length > 0) {
            try {
                await saveVirtualAnnouncements(local);
            } catch (e) {
                pushedOk = false;
            }
        }

        const remote = await listVirtualAnnouncements();
        if (!Array.isArray(remote)) {
            return;
        }

        // Soft-deleted (status "N") announcements neither schedule nor belong in
        // the list, so drop them from the local store.
        const active = remote.filter((item) => item?.id && item.status !== "N");

        if (pushedOk) {
            // Backend is authoritative — replace local wholesale so removals on
            // other devices are reflected here too.
            setAnnouncementSchedules(active);
        } else {
            // Couldn't confirm local-only items reached the backend, so merge
            // rather than replace to avoid dropping unsynced announcements.
            const byId = new Map(readAnnouncementSchedules().map((item) => [item.id, item]));
            active.forEach((item) => {
                if (!byId.has(item.id)) {
                    byId.set(item.id, item);
                }
            });
            setAnnouncementSchedules(Array.from(byId.values()));
        }
    } catch (e) {
        // A failed fetch leaves local schedules untouched so playback continues.
        console.warn("Virtual announcement login hydrate failed:", e);
    }
};

/* --------------------------- recording meta -------------------------- */

export const readRecordingMeta = () => {
    try {
        const data = JSON.parse(localStorage.getItem(ANNOUNCEMENT_RECORDINGS_META_KEY));
        return Array.isArray(data) ? data : [];
    } catch (e) {
        console.error("Error reading recording metadata from local storage:", e);
        return [];
    }
};

export const notifyRecordingsUpdated = (recordings = readRecordingMeta()) => {
    window.dispatchEvent(
        new CustomEvent(ANNOUNCEMENT_RECORDINGS_UPDATED_EVENT, {
            detail: {recordings, updatedAt: Date.now()},
        })
    );
};

const setRecordingMeta = (recordings = []) => {
    const next = Array.isArray(recordings) ? recordings : [];
    localStorage.setItem(ANNOUNCEMENT_RECORDINGS_META_KEY, JSON.stringify(next));
    notifyRecordingsUpdated(next);
    return next;
};

/* ------------------------------ IndexedDB ---------------------------- */

const openDb = () => {
    return new Promise((resolve, reject) => {
        if (typeof indexedDB === "undefined") {
            reject(new Error("IndexedDB is not available in this browser."));
            return;
        }

        const request = indexedDB.open(IDB_NAME, IDB_VERSION);

        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(IDB_STORE)) {
                db.createObjectStore(IDB_STORE, {keyPath: "id"});
            }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
};

const idbPut = async (record) => {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, "readwrite");
        tx.objectStore(IDB_STORE).put(record);
        tx.oncomplete = () => {
            db.close();
            resolve();
        };
        tx.onerror = () => {
            db.close();
            reject(tx.error);
        };
    });
};

const idbGet = async (id) => {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, "readonly");
        const request = tx.objectStore(IDB_STORE).get(id);
        request.onsuccess = () => {
            db.close();
            resolve(request.result || null);
        };
        request.onerror = () => {
            db.close();
            reject(request.error);
        };
    });
};

const idbDelete = async (id) => {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, "readwrite");
        tx.objectStore(IDB_STORE).delete(id);
        tx.oncomplete = () => {
            db.close();
            resolve();
        };
        tx.onerror = () => {
            db.close();
            reject(tx.error);
        };
    });
};

/* ----------------------------- recordings ---------------------------- */

export const saveRecording = async ({id, name, blob, mimeType, duration, url}) => {
    const recordingId = id || `rec-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    await idbPut({id: recordingId, blob, mimeType});

    const meta = readRecordingMeta();
    const entry = {
        id: recordingId,
        name: name || `Recording ${meta.length + 1}`,
        mimeType,
        duration: duration || 0,
        size: blob?.size || 0,
        // Remote (server) URL once the audio has been uploaded; null until then.
        url: url || null,
        createdAt: Date.now(),
    };
    const index = meta.findIndex((item) => item.id === recordingId);
    if (index >= 0) {
        meta[index] = {...meta[index], ...entry};
    } else {
        meta.push(entry);
    }
    setRecordingMeta(meta);
    return entry;
};

export const getRecordingBlob = async (id) => {
    const record = await idbGet(id);
    return record ? record.blob : null;
};

export const deleteRecording = async (id) => {
    try {
        await idbDelete(id);
    } catch (e) {
        console.error("Error deleting recording blob:", e);
    }
    setRecordingMeta(readRecordingMeta().filter((item) => item.id !== id));
    // Drop any schedules that referenced this recording.
    const schedules = readAnnouncementSchedules();
    const remaining = schedules.filter((item) => item.recordingId !== id);
    if (remaining.length !== schedules.length) {
        setAnnouncementSchedules(remaining);
    }
};

export const renameRecording = (id, name) => {
    const meta = readRecordingMeta().map((item) => (item.id === id ? {...item, name} : item));
    return setRecordingMeta(meta);
};

// Attach the server (remote) URL to a recording once its async upload finishes.
export const setRecordingUrl = (id, url) => {
    const meta = readRecordingMeta().map((item) => (item.id === id ? {...item, url} : item));
    return setRecordingMeta(meta);
};

/* --------------------------- broadcast history ----------------------- */
//
// Every time an announcement actually goes on air (manual "Broadcast now" or a
// scheduled play) we record a lightweight entry here so it can be replayed from
// the History tab. Text entries carry everything needed to re-speak them;
// recording entries just reference the blob already stored in IndexedDB.

export const readBroadcastHistory = () => {
    try {
        const data = JSON.parse(localStorage.getItem(ANNOUNCEMENT_HISTORY_STORAGE_KEY));
        return Array.isArray(data) ? data : [];
    } catch (e) {
        console.error("Error reading broadcast history from local storage:", e);
        return [];
    }
};

export const notifyBroadcastHistoryUpdated = (history = readBroadcastHistory()) => {
    window.dispatchEvent(
        new CustomEvent(ANNOUNCEMENT_HISTORY_UPDATED_EVENT, {
            detail: {history, updatedAt: Date.now()},
        })
    );
};

const setBroadcastHistory = (history = []) => {
    const next = (Array.isArray(history) ? history : []).slice(0, HISTORY_LIMIT);
    localStorage.setItem(ANNOUNCEMENT_HISTORY_STORAGE_KEY, JSON.stringify(next));
    notifyBroadcastHistoryUpdated(next);
    return next;
};

export const addBroadcastHistoryEntry = (entry = {}) => {
    const record = {
        id: `bc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        broadcastAt: Date.now(),
        source: "manual",
        type: "text",
        ...entry,
    };
    return setBroadcastHistory([record, ...readBroadcastHistory()]);
};

export const deleteBroadcastHistoryEntry = (id) => {
    return setBroadcastHistory(readBroadcastHistory().filter((item) => item.id !== id));
};

export const clearBroadcastHistory = () => setBroadcastHistory([]);
